import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { FrameworkConnectionStats } from '../../types/analytics';

interface FrameworkConnectionChartProps {
  data: FrameworkConnectionStats[];
  limit?: number;
}

export const FrameworkConnectionChart: React.FC<FrameworkConnectionChartProps> = ({ 
  data, 
  limit = 10 
}) => {
  const topFrameworks = data.slice(0, limit);
  const maxConnections = Math.max(...topFrameworks.map(f => f.connectionCount), 1);

  return (
    <div className="space-y-3">
      {topFrameworks.map((framework, index) => {
        const barWidth = (framework.connectionCount / maxConnections) * 100;
        
        return (
          <div key={framework.frameworkId} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <Link
                to={`/elevation/entry/${framework.frameworkId}`}
                className="font-medium text-charcoal-900 hover:text-primary-600 transition-colors flex items-center gap-2 group"
              >
                <span className="text-charcoal-500">#{index + 1}</span>
                <span className="group-hover:underline">{framework.frameworkName}</span>
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <span className="font-semibold text-primary-700">
                {framework.connectionCount}
              </span>
            </div>
            
            <div className="relative h-6 bg-[var(--hover-tint)] rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary-400 to-sage-400 rounded-full transition-all duration-500"
                style={{ width: `${barWidth}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                <span className="text-charcoal-700">
                  ↓ {framework.inboundConnections} | ↑ {framework.outboundConnections}
                </span>
                <span className="text-charcoal-600 font-medium">
                  Score: {framework.centralityScore}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      
      {data.length === 0 && (
        <div className="text-center py-8 text-charcoal-500">
          <p>No framework connections data available yet.</p>
          <p className="text-sm mt-2">Start exploring frameworks to see connections!</p>
        </div>
      )}
    </div>
  );
};

